import { Alert, Tag } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";

const SECTION_KEYS = [
  { key: "unresolved", labelKey: "SECTIONS.UNRESOLVED", color: "red" },
  { key: "ambiguous", labelKey: "SECTIONS.AMBIGUOUS", color: "orange" },
  { key: "manual", labelKey: "SECTIONS.MANUAL", color: "gold" },
  { key: "materialized", labelKey: "SECTIONS.MATERIALIZED", color: "blue" },
  { key: "bound", labelKey: "SECTIONS.BOUND", color: "green" },
];

const entryText = (entry) =>
  [entry.dataField, entry.reason || entry.via || entry.note]
    .filter(Boolean)
    .join(" — ");

const BindingReview = ({ report }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const BINDING = t("BINDING", { returnObjects: true });

  if (!report) {
    return (
      <div className="screen-studio__versions">
        <h4>{BINDING.TITLE}</h4>
        <p className="binding-review__empty">{BINDING.EMPTY}</p>
      </div>
    );
  }

  const warnings = report.warnings || [];

  return (
    <div className="screen-studio__versions binding-review">
      <h4>{BINDING.TITLE}</h4>

      {report.ok ? (
        <Alert
          type="success"
          showIcon
          message={BINDING.COMPLETE}
          className="binding-review__alert"
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message={BINDING.INCOMPLETE}
          className="binding-review__alert"
        />
      )}

      {warnings.map((warning) => (
        <Alert
          key={warning}
          type="error"
          showIcon
          message={warning}
          className="binding-review__alert-compact"
        />
      ))}

      {SECTION_KEYS.map((section) => {
        const items = report[section.key] || [];
        if (items.length === 0) return null;
        return (
          <div key={section.key} className="binding-review__section">
            <div className="binding-review__section-header">
              <Tag color={section.color} className="binding-review__tag">
                {`${t(`BINDING.${section.labelKey}`)} (${items.length})`}
              </Tag>
            </div>
            {items.map((entry, index) => (
              <div
                key={`${section.key}-${entry.dataField}-${index}`}
                className="binding-review__entry"
                title={entryText(entry)}
              >
                {entryText(entry)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

BindingReview.propTypes = {
  report: PropTypes.shape({
    ok: PropTypes.bool,
    warnings: PropTypes.arrayOf(PropTypes.string),
    unresolved: PropTypes.array,
    ambiguous: PropTypes.array,
    manual: PropTypes.array,
    materialized: PropTypes.array,
    bound: PropTypes.array,
  }),
};

BindingReview.defaultProps = {
  report: null,
};

export const isPublishBlocked = (report) => {
  if (!report) return false;
  const unresolved = report.unresolved || [];
  const ambiguous = report.ambiguous || [];
  return unresolved.length > 0 || ambiguous.length > 0;
};

export default BindingReview;
